import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResumeUpload from '../Auth/ResumeUpload';
import { Job } from '../../types/JobTypes';
import { savedJobsService } from '../../services/savedJobs.service';
import { referralsService, Referral, ReferredPerson } from '../../services/referrals.service';
import { savedCompaniesService } from '../../services/savedCompanies.service';
import ReferralForm from './ReferralForm';
import JobDetail from '../JobDetail';
import CompanyDetailsModal from './CompanyDetailsModal';
import ReferralRequests from './ReferralRequests';
import './ProfileSettings.css';

interface ProfileSettingsProps {
  darkMode?: boolean;
  onClose?: () => void;
  onNavigate?: (page: string, data?: any) => void;
  initialTab?: 'profile' | 'resume' | 'saved-jobs' | 'saved-companies' | 'referrals' | 'preferences' | 'privacy';
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ 
  darkMode = false,
  onClose,
  onNavigate,
  initialTab = 'profile'
}) => {
  const { user, updateProfile, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'resume' | 'saved-jobs' | 'saved-companies' | 'referrals' | 'preferences' | 'privacy'>(initialTab);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Update active tab when initialTab prop changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  // Profile form state
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    skills: user?.skills || [],
    salaryMin: user?.salaryRange?.min || 0,
    salaryMax: user?.salaryRange?.max || 200000
  });

  // Preferences state
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    jobAlerts: true,
    marketingEmails: false,
    profileVisibility: 'public' as 'public' | 'private' | 'recruiters-only'
  });

  // Saved jobs state
  const [savedJobs, setSavedJobs] = useState<Job[]>([]);
  const [isLoadingSavedJobs, setIsLoadingSavedJobs] = useState(false);
  
  // Saved companies state
  const [savedCompanies, setSavedCompanies] = useState<any[]>([]);
  const [isLoadingSavedCompanies, setIsLoadingSavedCompanies] = useState(false);
  
  // Referrals state
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  
  // Referred people state
  const [referredPeople, setReferredPeople] = useState<ReferredPerson[]>([]);
  const [isLoadingReferredPeople, setIsLoadingReferredPeople] = useState(false);
  const [showReferralForm, setShowReferralForm] = useState(false);
  
  // Modal states
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  
  // Resume builder state
  const [resumeData, setResumeData] = useState({
    summary: '',
    experience: [] as Array<{
      id: string;
      title: string;
      company: string;
      location: string;
      startDate: string;
      endDate: string;
      current: boolean;
      description: string;
    }>,
    education: [] as Array<{
      id: string;
      degree: string;
      field: string;
      school: string;
      location: string;
      startDate: string;
      endDate: string;
      gpa?: string;
    }>,
    certifications: [] as Array<{
      id: string;
      name: string;
      issuer: string;
      date: string;
      expiryDate?: string;
      credentialId?: string;
    }>
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        skills: user.skills || [],
        salaryMin: user.salaryRange?.min || 0,
        salaryMax: user.salaryRange?.max || 200000
      });
      
      // Load saved jobs, companies and referrals
      loadSavedJobs();
      loadSavedCompanies();
      loadReferrals();
      loadReferredPeople();
    }
  }, [user]);
  
  const loadSavedJobs = async () => {
    if (!user?.id) return;
    
    setIsLoadingSavedJobs(true);
    try {
      const jobs = await savedJobsService.getSavedJobs(user.id);
      setSavedJobs(jobs);
    } catch (error) {
      // console.error('Error loading saved jobs:', error);
    } finally {
      setIsLoadingSavedJobs(false);
    }
  };
  
  const loadReferrals = async () => {
    if (!user?.id) return;
    
    setIsLoadingReferrals(true);
    try {
      const userReferrals = await referralsService.getUserReferrals(user.id);
      setReferrals(userReferrals);
    } catch (error) {
      // console.error('Error loading referrals:', error);
    } finally {
      setIsLoadingReferrals(false);
    }
  };
  
  const loadSavedCompanies = async () => {
    if (!user?.id) return;
    
    setIsLoadingSavedCompanies(true);
    try {
      const companies = await savedCompaniesService.getSavedCompanies(user.id);
      setSavedCompanies(companies);
    } catch (error) {
      // console.error('Error loading saved companies:', error);
    } finally {
      setIsLoadingSavedCompanies(false);
    }
  };
  
  const loadReferredPeople = async () => {
    if (!user?.id) return;
    
    setIsLoadingReferredPeople(true);
    try {
      const people = await referralsService.getReferredPeople(user.id);
      setReferredPeople(people);
    } catch (error) {
      // console.error('Error loading referred people:', error);
    } finally {
      setIsLoadingReferredPeople(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillsChange = (newSkills: string[]) => {
    setFormData(prev => ({
      ...prev,
      skills: newSkills
    }));
  };

  const handleAddSkill = (skill: string) => {
    if (skill && !formData.skills.includes(skill)) {
      handleSkillsChange([...formData.skills, skill]);
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    handleSkillsChange(formData.skills.filter(skill => skill !== skillToRemove));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setSuccessMessage(null);
    
    try {
      await updateProfile({
        name: formData.name,
        skills: formData.skills,
        salaryRange: {
          min: Number(formData.salaryMin),
          max: Number(formData.salaryMax)
        }
      });
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceChange = (key: keyof typeof preferences, value: any) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={`profile-settings ${darkMode ? 'dark' : 'light'}`}>
        <div className="settings-content">
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <h3 style={{ marginBottom: '16px' }}>Please log in to access your profile</h3>
            <p style={{ color: 'var(--text-secondary)' }}>You need to be logged in to view and edit your profile settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`profile-settings ${darkMode ? 'dark' : 'light'}`}>
      <div className="settings-header">
        <h2>Settings</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose} aria-label="Close settings">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button 
          className={`tab ${activeTab === 'resume' ? 'active' : ''}`}
          onClick={() => setActiveTab('resume')}
        >
          Resume Builder
        </button>
        <button 
          className={`tab ${activeTab === 'saved-jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved-jobs')}
        >
          Saved Jobs
        </button>
        <button 
          className={`tab ${activeTab === 'saved-companies' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved-companies')}
        >
          Saved Companies
        </button>
        <button 
          className={`tab ${activeTab === 'referrals' ? 'active' : ''}`}
          onClick={() => setActiveTab('referrals')}
        >
          My Referrals
        </button>
        <button 
          className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          Preferences
        </button>
        <button 
          className={`tab ${activeTab === 'privacy' ? 'active' : ''}`}
          onClick={() => setActiveTab('privacy')}
        >
          Privacy
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="section-title">Basic Information</div>
            
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                disabled
                className="form-input disabled"
              />
              <p className="field-hint">Email cannot be changed</p>
            </div>

            <div className="section-title">Skills</div>
            <div className="skills-section">
              <div className="skills-input-wrapper">
                <input
                  type="text"
                  placeholder="Add a skill (e.g., React, Python)"
                  className="skills-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSkill((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
              </div>
              <div className="skills-list">
                {formData.skills.map(skill => (
                  <span key={skill} className="skill-tag">
                    {skill}
                    <button 
                      className="remove-skill"
                      onClick={() => handleRemoveSkill(skill)}
                      aria-label={`Remove ${skill}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="section-title">Salary Expectations</div>
            <div className="salary-range">
              <div className="form-group">
                <label htmlFor="salaryMin">Minimum ($)</label>
                <input
                  type="number"
                  id="salaryMin"
                  name="salaryMin"
                  value={formData.salaryMin}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  step="1000"
                />
              </div>
              <div className="form-group">
                <label htmlFor="salaryMax">Maximum ($)</label>
                <input
                  type="number"
                  id="salaryMax"
                  name="salaryMax"
                  value={formData.salaryMax}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  step="1000"
                />
              </div>
            </div>

            <div className="section-title">Resume</div>
            <ResumeUpload darkMode={darkMode} />

            <div className="form-actions">
              <button 
                className="save-btn"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {successMessage && (
                <span className="success-message">{successMessage}</span>
              )}
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="preferences-section">
            <div className="section-title">Email Notifications</div>
            
            <label className="toggle-option">
              <input
                type="checkbox"
                checked={preferences.emailNotifications}
                onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
              />
              <span className="toggle-label">
                <span className="label-text">Email Notifications</span>
                <span className="label-description">Receive important updates about your account</span>
              </span>
            </label>

            <label className="toggle-option">
              <input
                type="checkbox"
                checked={preferences.jobAlerts}
                onChange={(e) => handlePreferenceChange('jobAlerts', e.target.checked)}
              />
              <span className="toggle-label">
                <span className="label-text">Job Alerts</span>
                <span className="label-description">Get notified about new jobs matching your preferences</span>
              </span>
            </label>

            <label className="toggle-option">
              <input
                type="checkbox"
                checked={preferences.marketingEmails}
                onChange={(e) => handlePreferenceChange('marketingEmails', e.target.checked)}
              />
              <span className="toggle-label">
                <span className="label-text">Marketing Emails</span>
                <span className="label-description">Receive news and special offers from Jobometry</span>
              </span>
            </label>

            <div className="section-title">Profile Visibility</div>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="visibility"
                  checked={preferences.profileVisibility === 'public'}
                  onChange={() => handlePreferenceChange('profileVisibility', 'public')}
                />
                <span className="radio-label">
                  <span className="label-text">Public</span>
                  <span className="label-description">Anyone can view your profile</span>
                </span>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="visibility"
                  checked={preferences.profileVisibility === 'recruiters-only'}
                  onChange={() => handlePreferenceChange('profileVisibility', 'recruiters-only')}
                />
                <span className="radio-label">
                  <span className="label-text">Recruiters Only</span>
                  <span className="label-description">Only verified recruiters can view your profile</span>
                </span>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="visibility"
                  checked={preferences.profileVisibility === 'private'}
                  onChange={() => handlePreferenceChange('profileVisibility', 'private')}
                />
                <span className="radio-label">
                  <span className="label-text">Private</span>
                  <span className="label-description">Your profile is hidden from everyone</span>
                </span>
              </label>
            </div>
          </div>
        )}

        {activeTab === 'resume' && (
          <div className="resume-builder-section">
            <div className="section-title">Professional Summary</div>
            <textarea
              className="form-textarea"
              placeholder="Write a brief professional summary..."
              value={resumeData.summary}
              onChange={(e) => setResumeData(prev => ({ ...prev, summary: e.target.value }))}
              rows={4}
            />

            <div className="section-title">Work Experience</div>
            <div className="experience-list">
              {resumeData.experience.map((exp) => (
                <div key={exp.id} className="experience-item">
                  <div className="experience-header">
                    <h4>{exp.title}</h4>
                    <button className="remove-btn" onClick={() => {
                      setResumeData(prev => ({
                        ...prev,
                        experience: prev.experience.filter(e => e.id !== exp.id)
                      }));
                    }}>Remove</button>
                  </div>
                  <p className="experience-company">{exp.company} • {exp.location}</p>
                  <p className="experience-dates">
                    {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                  </p>
                  <p className="experience-description">{exp.description}</p>
                </div>
              ))}
              <button className="add-btn" onClick={() => {
                const newExp = {
                  id: Date.now().toString(),
                  title: '',
                  company: '',
                  location: '',
                  startDate: '',
                  endDate: '',
                  current: false,
                  description: ''
                };
                setResumeData(prev => ({
                  ...prev,
                  experience: [...prev.experience, newExp]
                }));
              }}>+ Add Experience</button>
            </div>

            <div className="section-title">Education</div>
            <div className="education-list">
              {resumeData.education.map((edu) => (
                <div key={edu.id} className="education-item">
                  <div className="education-header">
                    <h4>{edu.degree} in {edu.field}</h4>
                    <button className="remove-btn" onClick={() => {
                      setResumeData(prev => ({
                        ...prev,
                        education: prev.education.filter(e => e.id !== edu.id)
                      }));
                    }}>Remove</button>
                  </div>
                  <p className="education-school">{edu.school} • {edu.location}</p>
                  <p className="education-dates">{edu.startDate} - {edu.endDate}</p>
                  {edu.gpa && <p className="education-gpa">GPA: {edu.gpa}</p>}
                </div>
              ))}
              <button className="add-btn" onClick={() => {
                const newEdu = {
                  id: Date.now().toString(),
                  degree: '',
                  field: '',
                  school: '',
                  location: '',
                  startDate: '',
                  endDate: '',
                  gpa: ''
                };
                setResumeData(prev => ({
                  ...prev,
                  education: [...prev.education, newEdu]
                }));
              }}>+ Add Education</button>
            </div>

            <div className="section-title">Certifications</div>
            <div className="certifications-list">
              {resumeData.certifications.map((cert) => (
                <div key={cert.id} className="certification-item">
                  <div className="certification-header">
                    <h4>{cert.name}</h4>
                    <button className="remove-btn" onClick={() => {
                      setResumeData(prev => ({
                        ...prev,
                        certifications: prev.certifications.filter(c => c.id !== cert.id)
                      }));
                    }}>Remove</button>
                  </div>
                  <p className="certification-issuer">{cert.issuer}</p>
                  <p className="certification-date">Issued: {cert.date}</p>
                  {cert.expiryDate && <p className="certification-expiry">Expires: {cert.expiryDate}</p>}
                  {cert.credentialId && <p className="certification-id">Credential ID: {cert.credentialId}</p>}
                </div>
              ))}
              <button className="add-btn" onClick={() => {
                const newCert = {
                  id: Date.now().toString(),
                  name: '',
                  issuer: '',
                  date: '',
                  expiryDate: '',
                  credentialId: ''
                };
                setResumeData(prev => ({
                  ...prev,
                  certifications: [...prev.certifications, newCert]
                }));
              }}>+ Add Certification</button>
            </div>
          </div>
        )}

        {activeTab === 'saved-jobs' && (
          <div className="saved-jobs-section">
            <div className="section-title">Your Saved Jobs</div>
            {isLoadingSavedJobs ? (
              <div className="loading-state">
                <p>Loading your saved jobs...</p>
              </div>
            ) : savedJobs.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="empty-icon">
                  <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 16L32 32M32 16L16 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>No saved jobs yet</h3>
                <p>Jobs you save will appear here for easy access</p>
              </div>
            ) : (
              <div className="saved-jobs-list">
                {savedJobs.map((job) => (
                  <div key={job.id} className="saved-job-item">
                    <div className="job-info">
                      <h4>{job.title}</h4>
                      <p>{job.company} • {job.location}</p>
                      <p className="job-salary">${job.salary.toLocaleString()}</p>
                    </div>
                    <div className="job-actions">
                      <button className="view-btn" onClick={() => {
                        setSelectedJob(job);
                      }}>View</button>
                      <button className="remove-btn" onClick={async () => {
                        if (user?.id) {
                          await savedJobsService.removeSavedJob(user.id, job.id);
                          setSavedJobs(prev => prev.filter(j => j.id !== job.id));
                        }
                      }}>Remove</button>
                      <div className="coming-soon-btn-wrapper">
                        <button className="enhance-cv-btn" disabled>
                          Enhance my CV
                        </button>
                        <span className="coming-soon-text">Coming Soon</span>
                      </div>
                      <div className="coming-soon-btn-wrapper">
                        <button className="ai-apply-btn" disabled>
                          AI Apply
                        </button>
                        <span className="coming-soon-text">Coming Soon</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'saved-companies' && (
          <div className="saved-companies-section">
            <div className="section-title">Saved Companies</div>
            {isLoadingSavedCompanies ? (
              <div className="loading-state">
                <p>Loading saved companies...</p>
              </div>
            ) : savedCompanies.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="empty-icon">
                  <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M24 14V24L30 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <h3>No saved companies yet</h3>
                <p>Companies you save will appear here for easy access</p>
              </div>
            ) : (
              <div className="saved-companies-list">
                {savedCompanies.map((company) => (
                  <div key={company.id} className="saved-company-item">
                    <div className="company-info">
                      <h4>{company.name}</h4>
                      <p>{company.industry} • {company.size} Company</p>
                      <div className="company-rating">
                        <span className="star">★</span>
                        <span>{company.globalRating.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="company-actions">
                      <button className="view-btn" onClick={() => {
                        setSelectedCompany(company);
                      }}>View</button>
                      <button className="remove-btn" onClick={async () => {
                        if (user?.id) {
                          await savedCompaniesService.removeSavedCompany(user.id, company.id);
                          setSavedCompanies(prev => prev.filter(c => c.id !== company.id));
                        }
                      }}>Remove</button>
                      <button className="wanna-work-btn" onClick={async () => {
                        if (user?.id) {
                          try {
                            await referralsService.submitReferral(
                              user.id,
                              company.id,
                              company.name,
                              'I am interested in working at this company'
                            );
                            alert('Your interest has been submitted!');
                          } catch (error) {
                            console.error('Error submitting interest:', error);
                            alert('Failed to submit interest. Please try again.');
                          }
                        }
                      }}>Wanna Work Here</button>
                      <div className="coming-soon-btn-wrapper">
                        <button className="enhance-cv-btn" disabled>
                          Enhance CV
                        </button>
                        <span className="coming-soon-text">Coming Soon</span>
                      </div>
                      <div className="coming-soon-btn-wrapper">
                        <button className="profile-match-btn" disabled>
                          Match Score
                        </button>
                        <span className="coming-soon-text">Coming Soon</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'referrals' && (
          <div className="referrals-section">
            {/* Enhanced Referral Requests Section */}
            <ReferralRequests 
              userId={user?.id || ''} 
              userCompanyId={user?.companyId}
              userCompanyName={user?.companyName}
              userName={user?.name || 'Employee'}
              darkMode={darkMode}
            />
            
            <div className="section-title" style={{ marginTop: '32px' }}>Companies I Want to Work For</div>
            {isLoadingReferrals ? (
              <div className="loading-state">
                <p>Loading your referrals...</p>
              </div>
            ) : referrals.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="empty-icon">
                  <path d="M20 22C24.4183 22 28 18.4183 28 14C28 9.58172 24.4183 6 20 6C15.5817 6 12 9.58172 12 14C12 18.4183 15.5817 22 20 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 42V38C4 35.8783 4.84285 33.8434 6.34315 32.3431C7.84344 30.8429 9.87827 30 12 30H28C30.1217 30 32.1566 30.8429 33.6569 32.3431C35.1571 33.8434 36 35.8783 36 38V42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M32 22L36 26L44 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>No referrals yet</h3>
                <p>Companies you've expressed interest in will appear here</p>
              </div>
            ) : (
              <div className="referrals-list">
                {referrals.map((referral) => (
                  <div key={referral.id} className="referral-item">
                    <div className="referral-info">
                      <h4>{referral.companyName}</h4>
                      <p className="referral-date">
                        Submitted: {new Date(referral.submittedAt).toLocaleDateString()}
                      </p>
                      {referral.notes && (
                        <p className="referral-notes">{referral.notes}</p>
                      )}
                    </div>
                    <div className={`referral-status status-${referral.status.toLowerCase()}`}>
                      {referral.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="section-title" style={{ marginTop: '32px' }}>People I've Referred</div>
            {isLoadingReferredPeople ? (
              <div className="loading-state">
                <p>Loading referred people...</p>
              </div>
            ) : referredPeople.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="empty-icon">
                  <path d="M16 21C20.4183 21 24 17.4183 24 13C24 8.58172 20.4183 5 16 5C11.5817 5 8 8.58172 8 13C8 17.4183 11.5817 21 16 21Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M32 15C34.2091 15 36 13.2091 36 11C36 8.79086 34.2091 7 32 7C29.7909 7 28 8.79086 28 11C28 13.2091 29.7909 15 32 15Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M1 39V37C1 33.134 4.134 30 8 30H24C27.866 30 31 33.134 31 37V39" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M36 30C39.866 30 43 33.134 43 37V39" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <h3>No referred people yet</h3>
                <p>People you refer to companies will appear here</p>
                <button className="add-btn" onClick={() => setShowReferralForm(true)}>
                  + Refer Someone
                </button>
              </div>
            ) : (
              <>
                <div className="referred-people-list">
                  {referredPeople.map((person) => (
                    <div key={person.id} className="referred-person-item">
                      <div className="person-info">
                        <h4>{person.candidateName}</h4>
                        <p className="person-details">
                          {person.position} at {person.companyName}
                        </p>
                        <p className="person-contact">
                          {person.candidateEmail} {person.candidatePhone && `• ${person.candidatePhone}`}
                        </p>
                        <p className="person-date">
                          Referred: {new Date(person.referredAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="person-status">
                        <span className={`status-badge status-${person.status.toLowerCase()}`}>
                          {person.status.replace('_', ' ')}
                        </span>
                        {person.referralBonus && (
                          <div className="bonus-info">
                            <span className="bonus-amount">${person.referralBonus}</span>
                            {person.bonusPaid && <span className="bonus-paid">✓ Paid</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button className="add-btn" style={{ marginTop: '16px' }} onClick={() => setShowReferralForm(true)}>
                  + Refer Someone
                </button>
              </>
            )}
          </div>
        )}

        {activeTab === 'privacy' && (
          <div className="privacy-section">
            <div className="section-title">Account Security</div>
            
            <div className="security-item">
              <div className="security-info">
                <h4>Login Provider</h4>
                <p>You're logged in with {user?.provider || 'OAuth'}</p>
              </div>
            </div>

            <div className="security-item">
              <div className="security-info">
                <h4>Two-Factor Authentication</h4>
                <p>Add an extra layer of security to your account</p>
              </div>
              <button className="secondary-btn">Enable 2FA</button>
            </div>

            <div className="section-title">Data Management</div>
            
            <div className="data-item">
              <div className="data-info">
                <h4>Download Your Data</h4>
                <p>Get a copy of all your Jobometry data</p>
              </div>
              <button className="secondary-btn">Request Download</button>
            </div>

            <div className="data-item danger">
              <div className="data-info">
                <h4>Delete Account</h4>
                <p>Permanently delete your account and all associated data</p>
              </div>
              <button className="danger-btn">Delete Account</button>
            </div>
          </div>
        )}
      </div>

      {showReferralForm && user?.id && (
        <ReferralForm
          referrerId={user.id}
          onClose={() => setShowReferralForm(false)}
          onSuccess={() => {
            setShowReferralForm(false);
            loadReferredPeople();
          }}
          darkMode={darkMode}
        />
      )}
      
      {selectedJob && (
        <JobDetail
          jobId={selectedJob.id}
          darkMode={darkMode}
          onClose={() => setSelectedJob(null)}
        />
      )}
      
      {selectedCompany && (
        <CompanyDetailsModal
          company={selectedCompany}
          darkMode={darkMode}
          onClose={() => setSelectedCompany(null)}
        />
      )}
    </div>
  );
};

export default ProfileSettings;